import {
  PRODUCT_ROUTES,
  productListResponseSchema,
  productResponseSchema,
  type Product,
  type ProductList,
} from "@nh/shared";
import { apiFetch } from "@/shared/api/client";

export const shopProductsQueryKey = ["shop", "products"] as const;

/** La clave de UNA ficha. Lleva el slug dentro: cada producto, su caché. */
export function shopProductQueryKey(slug: string): readonly [string, string, string] {
  return ["shop", "product", slug];
}

/**
 * Cuántos productos se piden de una vez.
 *
 * El catálogo de la tienda cabe holgadamente en una página: pedirlo entero
 * permite filtrar y ordenar en el navegador, sin un viaje al servidor por
 * cada clic en un chip de categoría. `productListQuerySchema` topa este
 * valor en 100, así que 100 es el máximo que el contrato acepta; el día
 * que el catálogo lo pase, esta pantalla necesita paginación de verdad y
 * no un número más grande aquí.
 */
export const SHOP_PAGE_SIZE = 100;

/**
 * GET /api/products — el catálogo PÚBLICO (solo `published`).
 *
 * `PRODUCT_ROUTES.publicList` ya trae el prefijo `/api`, de ahí el
 * `baseUrl: ""`: si no, `apiFetch` lo antepondría otra vez y la petición
 * saldría a `/api/api/products`.
 */
export function fetchShopProducts(): Promise<ProductList> {
  return apiFetch(
    `${PRODUCT_ROUTES.publicList}?perPage=${SHOP_PAGE_SIZE}`,
    productListResponseSchema,
    { baseUrl: "" },
  );
}

/**
 * GET /api/products/:slug — la ficha PÚBLICA de un producto.
 *
 * Responde 404 con `NOT_FOUND` cuando el producto no existe o no está
 * publicado. Los dos casos se ven igual desde fuera a propósito: decir
 * "existe pero está en borrador" filtraría el catálogo que todavía no se
 * ha anunciado.
 *
 * El slug se codifica antes de meterlo en la ruta. Hoy viene de un
 * parámetro de la URL, así que puede traer cualquier cosa que alguien
 * escriba en la barra de direcciones — y una barra o un signo de
 * interrogación sin codificar cambiarían a qué endpoint se está llamando.
 */
export function fetchShopProduct(slug: string): Promise<{ product: Product }> {
  return apiFetch(
    PRODUCT_ROUTES.publicBySlug(encodeURIComponent(slug)),
    productResponseSchema,
    { baseUrl: "" },
  );
}
